/**
 * SettingsHub — Shopify-style settings index for the Website Builder.
 *
 * Tier 1 (build now): General · Plan & Billing · Domains · Shipping &
 * Delivery · Customer accounts · Notifications · Policies · Payments
 *
 * Each card opens its own dedicated settings sub-page. Pages are stubs for
 * now — the nav shell exists, sub-pages render with a clean header and a
 * "Coming soon · Wave 2" inline notice when wiring isn't built yet.
 *
 * Color theme stays consistent with the rest of the builder: midnight
 * indigo + violet/fuchsia accents (NOT Shopify white).
 */
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Settings as SettingsIcon, CreditCard, Globe, Truck, Users, Bell,
  ScrollText, Wallet, ArrowRight, Lock, Mail, Receipt, MapPin, Languages,
  KeyRound, ShoppingBag, FileBadge, Layers, ArrowLeft, Plug, Heart, Download,
} from 'lucide-react';
import {
  GlassCard, GradientIconTile, DashboardBackdrop,
} from '@/components/dashboard/dashboard-ui';

/* ─────────────────────────────────────────────────────────────
 *  Tier definitions
 *  status: 'ready' (link to existing page) · 'stub' (placeholder) · 'soon' (greyed)
 * ─────────────────────────────────────────────────────────── */
const TIER_1 = [
  { id: 'general',       icon: SettingsIcon, ramp: 'violet',  label: 'General',            desc: 'Brand name, logo, tagline, language.', to: '/website-builder/settings/general',       status: 'ready' },
  { id: 'billing',       icon: CreditCard,   ramp: 'fuchsia', label: 'Plan & Billing',     desc: 'Manage your subscription, plan, and payment method.', to: '/website-builder/settings/billing', status: 'ready' },
  { id: 'integrations',  icon: Plug,         ramp: 'cyan',    label: 'Integrations',       desc: 'Mailchimp, Google Analytics, Zapier, Slack, and more.', to: '/website-builder/settings/integrations', status: 'ready' },
  { id: 'team',          icon: Users,        ramp: 'lime',    label: 'Permissions & Ownership', desc: 'Owner profile, transfer ownership, invite contributors.', to: '/website-builder/settings/team',    status: 'ready' },
  { id: 'domains',       icon: Globe,        ramp: 'cyan',    label: 'Domains',            desc: 'Connect a custom domain or transfer one.', to: '/website-builder/settings/domains',   status: 'ready'  },
  { id: 'shipping',      icon: Truck,        ramp: 'amber',   label: 'Shipping & Delivery', desc: 'Rates, carriers, Pirate Ship labels.', to: '/website-builder/settings/shipping',    status: 'ready'  },
  { id: 'cart-recovery', icon: ShoppingBag,  ramp: 'amber',   label: 'Cart recovery',      desc: 'Automated emails to shoppers who leave items in their cart.', to: '/website-builder/settings/cart-recovery', status: 'ready' },
  { id: 'customer-accounts', icon: Users,    ramp: 'lime',    label: 'Customer accounts',  desc: 'Signup, login, profiles, addresses.',     to: '/website-builder/settings/customer-accounts', status: 'stub' },
  { id: 'notifications', icon: Bell,         ramp: 'violet',  label: 'Notifications',      desc: 'Email and SMS notification templates.',   to: '/website-builder/settings/notifications', status: 'stub' },
  { id: 'policies',      icon: ScrollText,   ramp: 'fuchsia', label: 'Policies',           desc: 'Returns, privacy, terms, contact info.',  to: '/website-builder/settings/policies',  status: 'stub'  },
  { id: 'payments',      icon: Wallet,       ramp: 'lime',    label: 'Payments & branded emails', desc: 'Connect Stripe for payouts and send emails from your own domain.',  to: '/website-builder/settings/payments-branding',         status: 'ready' },
  { id: 'donations',     icon: Heart,        ramp: 'fuchsia', label: 'Donations',          desc: 'Donation funds, amounts, recurring options, and forms.', to: '/website-builder/settings/donations', status: 'ready' },
  { id: 'account-controls', icon: Download,  ramp: 'cyan',    label: 'Account controls',   desc: 'Export your data anytime, or schedule deletion to move platforms.', to: '/website-builder/settings/account-controls', status: 'ready' },
];

const TIER_2 = [
  { id: 'checkout',        icon: ShoppingBag,  ramp: 'amber',   label: 'Checkout',             desc: 'Cart abandonment, custom fields, tipping.', status: 'soon' },
  { id: 'taxes',           icon: Receipt,      ramp: 'fuchsia', label: 'Taxes & duties',       desc: 'Tax rates, regions, tax-exempt customers.', status: 'soon' },
  { id: 'locations',       icon: MapPin,       ramp: 'violet',  label: 'Locations',            desc: 'Warehouses & pickup points.',                status: 'soon' },
  { id: 'customer-events', icon: Mail,         ramp: 'cyan',    label: 'Customer events',       desc: 'Track customer activity webhooks.',         status: 'soon' },
];

const TIER_3 = [
  { id: 'apps',          icon: Layers,    ramp: 'violet',  label: 'Apps & sales channels' },
  { id: 'metafields',    icon: FileBadge, ramp: 'fuchsia', label: 'Metafields & metaobjects' },
  { id: 'languages',     icon: Languages, ramp: 'cyan',    label: 'Languages' },
  { id: 'privacy',       icon: Lock,      ramp: 'amber',   label: 'Customer privacy' },
  { id: 'api',           icon: KeyRound,  ramp: 'lime',    label: 'API & developer keys' },
];

/* ─────────────────────────────────────────────────────────────
 *  Card
 * ─────────────────────────────────────────────────────────── */
function SettingsCard({ item, dim = false }) {
  const Inner = (
    <GlassCard glow={!dim} className={`p-5 h-full transition-all ${dim ? 'opacity-50' : 'hover:border-[#A78BFA]/40 cursor-pointer'}`}>
      <div className="flex items-start gap-4">
        <GradientIconTile icon={item.icon} ramp={item.ramp} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-white font-bold font-syne text-sm">{item.label}</p>
            {item.status === 'soon' && (
              <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-white/10 text-[#A5A8C9]">Soon</span>
            )}
            {item.status === 'stub' && (
              <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-400/25">Wave 2</span>
            )}
          </div>
          {item.desc && <p className="text-xs text-white/55 mt-0.5 leading-relaxed">{item.desc}</p>}
        </div>
        {!dim && <ArrowRight size={14} className="text-[#7B7FA8] shrink-0 mt-1" />}
      </div>
    </GlassCard>
  );
  if (dim || !item.to) return <div data-testid={`settings-card-${item.id}`}>{Inner}</div>;
  return <Link to={item.to} data-testid={`settings-card-${item.id}`} className="block">{Inner}</Link>;
}

/* ─────────────────────────────────────────────────────────────
 *  Settings hub index
 * ─────────────────────────────────────────────────────────── */
export default function SettingsHub() {
  return (
    <div className="relative" data-testid="settings-hub">
      <DashboardBackdrop />
      <div className="relative z-10 space-y-8 pb-12">
        {/* Header — Iter 168: removed the redundant purple "Settings" eyebrow
            (the page title "Site Settings" alone is enough context). */}
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white font-syne tracking-tight">Site Settings</h1>
          <p className="text-[#A5A8C9] text-sm mt-1.5">
            Configure your store — plan, domain, shipping, payouts, and everything in between.
          </p>
        </div>

        {/* Tier 1 — Essentials */}
        <section>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-3">Essentials</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {TIER_1.map((item) => <SettingsCard key={item.id} item={item} />)}
          </div>
        </section>

        {/* Tier 2 — Coming soon */}
        <section>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#A5A8C9] font-bold mb-3">Advanced · coming soon</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {TIER_2.map((item) => <SettingsCard key={item.id} item={item} dim />)}
          </div>
        </section>

        {/* Tier 3 — Backlog */}
        <section>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#7B7FA8] font-bold mb-3">Backlog</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {TIER_3.map((item) => (
              <div key={item.id} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.02] border border-white/[0.05] opacity-60">
                <GradientIconTile icon={item.icon} ramp={item.ramp} size="sm" />
                <span className="text-xs text-white/65">{item.label}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 *  SettingsSubPage — generic shell for sub-pages
 * ─────────────────────────────────────────────────────────── */
export function SettingsSubPage() {
  const { section } = useParams();
  const all = [...TIER_1, ...TIER_2];
  const item = all.find((x) => x.id === section);
  const title = item?.label || 'Settings';
  return (
    <div className="relative" data-testid={`settings-sub-${section || 'unknown'}`}>
      <DashboardBackdrop />
      <div className="relative z-10 space-y-6 pb-12 max-w-3xl">
        <Link to="/website-builder/settings" data-testid="settings-go-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-white hover:text-[#A78BFA] transition-colors font-semibold">
          <ArrowLeft size={12} /> Go Back
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-white font-syne tracking-tight">{title}</h1>
          {item?.desc && <p className="text-[#A5A8C9] text-sm mt-1.5">{item.desc}</p>}
        </div>

        <GlassCard glow className="p-10 text-center">
          <GradientIconTile icon={item?.icon || SettingsIcon} ramp={item?.ramp || 'violet'} size="lg" className="mx-auto" />
          <h2 className="text-xl font-bold text-white font-syne mt-5">Coming in Wave 2</h2>
          <p className="text-sm text-white/55 mt-2 max-w-md mx-auto leading-relaxed">
            The {title.toLowerCase()} settings are part of the next iteration. Once the money flows and 3rd-party integrations are wired, this page will surface controls inline.
          </p>
          <Link
            to="/website-builder/settings"
            className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-xs font-bold uppercase tracking-wider hover:shadow-[0_12px_30px_rgba(139,92,246,0.45)] transition-all"
          >
            Back to settings <ArrowRight size={13} />
          </Link>
        </GlassCard>
      </div>
    </div>
  );
}
